import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useUser } from '@plato/app-core/hooks/use-user';
import { CrmTaskStatus } from '@plato/data/prisma';
import { formatISOTimeForDisplay } from '@plato/shared-util/time-utils';
import CalendarIllustration from '@plato/ui-legacy/assets/illustrations/calendar.png';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { formatMonthLongDay } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import { formatISO } from 'date-fns';
import { CalendarIcon, CircleCheck } from 'lucide-react';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { CrmTaskDTO, CrmTaskFilterOptions } from '../../../server/dto/crm-task-dto.ts';
import { api } from '../../../utils/api.ts';
import { useAcl } from '../../acl/use-acl.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';
import { EventDetailsDialog as NewEventDetailsDialog } from '../../events-v2/components/event-details-dialog.tsx';
import {
    EventCreateDialog,
    type EventCreateDialogProps,
} from '../../events/components/event-create-dialog.tsx';
import { EventDetailsDialog } from '../../events/components/event-details-dialog.tsx';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';
import { TaskDueStatusText } from '../../tasks/components/task-due-status-text.tsx';
import { useShowCreateTaskDialog } from '../../tasks/hooks/use-show-create-task-dialog.ts';
import DashboardActivitiesList from '../components/dashboard-activities-list.tsx';
import { DashboardActivityItem } from '../components/dashboard-activity-item.tsx';
import { DataStatBlock } from '../components/data-stat-block.tsx';

export function DashboardTaskDataBlockItem() {
    const { t } = useTranslation();
    const locale = useResolvedLocale();
    const user = useUser();
    const { openTaskDetails } = useDialogs();
    const navigate = useNavigate();
    const acl = useAcl();
    const sendEvent = useAnalytics();
    const { enableNewEventPage } = useAppConfig();
    const showCreateTaskDialog = useShowCreateTaskDialog();

    const maxItems = useBreakpoint('xl') ? 5 : 4;

    const filters = useMemo(() => {
        return {
            assignedToIds: [user.id],
            status: CrmTaskStatus.OPEN,
        } satisfies CrmTaskFilterOptions;
    }, [user]);

    const requestDate = formatISO(new Date(), { representation: 'date' });

    const { data: tasksData, isLoading: isTasksLoading } =
        api.platoUser.query.tasks.getManyTasks.getListWithFilters.useQuery(filters);

    const areEventsEnabled = acl.can('list', 'CustomerEvent');

    const { data: eventsData, isLoading: isEventsLoading } = api.platoUser.query.events.getList.useQuery(
        {
            dateFrom: requestDate,
            dateTo: requestDate,
            ownerId: user.id,
        },
        {
            enabled: areEventsEnabled,
        },
    );

    const { visibleTasks, visibleEvents } = useMemo(() => {
        if (!tasksData) {
            return {
                visibleTasks: [],
                visibleEvents: [],
            };
        }

        const tasks = [...tasksData];
        const events = eventsData ? [...eventsData] : [];

        const visibleTasks: typeof tasks = [];
        const visibleEvents: typeof events = [];

        let slotsRemaining = eventsData ? maxItems - 1 : maxItems;

        while (slotsRemaining > 0 && (tasks.length > 0 || events.length > 0)) {
            const nextTask = tasks.shift();

            if (nextTask) {
                visibleTasks.push(nextTask);
                slotsRemaining--;
            }

            const nextEvent = events.shift();

            if (slotsRemaining > 0 && nextEvent) {
                visibleEvents.push(nextEvent);
                slotsRemaining--;
            }
        }

        return {
            visibleTasks,
            visibleEvents,
        };
    }, [maxItems, tasksData, eventsData]);

    const handleTaskClick = (task: CrmTaskDTO) => {
        sendEvent('home_activity_item_opened', { item_type: 'task' });
        openTaskDetails(task.taskId);
    };

    const handleCreateTask = () => {
        showCreateTaskDialog({ source: 'dashboard' });
    };

    const handleCreateEvent = () => {
        void DialogStateManager.show(EventCreateDialog, {
            analyticsSource: 'dashboard',
        } satisfies EventCreateDialogProps);
    };

    const taskCount = tasksData?.length || 0;
    const eventsCount = eventsData?.length || 0;
    const isLoading = areEventsEnabled ? isTasksLoading || isEventsLoading : isTasksLoading;
    const isEmpty = !Math.max(taskCount, eventsCount) && !isLoading;

    return (
        <DataStatBlock.Item className="h-full">
            <DataStatBlock.Body className="h-full" isLoading={isLoading}>
                <div className="flex h-full w-full overflow-hidden flex-col gap-2 xl:gap-3">
                    <Text color="neutral-700">
                        {t('today', { ns: 'common' })}
                        {', '}
                        {formatMonthLongDay(new Date(), locale)}
                    </Text>
                    {isEmpty && (
                        <div className="flex justify-center items-center w-full h-full">
                            <div className="flex flex-col items-center gap-6">
                                <img
                                    src={CalendarIllustration}
                                    alt={t('today', { ns: 'common' })}
                                    width={69}
                                    height={50}
                                />
                                <div className="flex flex-col items-center gap-2">
                                    <Text weight="semibold">
                                        {t('dashboard__empty_state__title', { ns: 'dashboard' })}
                                    </Text>
                                    <Text>
                                        {areEventsEnabled ? (
                                            <Trans
                                                i18nKey="dashboard__empty_state__subtitle"
                                                ns="dashboard"
                                                components={[
                                                    <LinkText
                                                        key="create-task"
                                                        as="button"
                                                        onClick={handleCreateTask}
                                                    />,
                                                    <LinkText
                                                        key="create-event"
                                                        as="button"
                                                        onClick={handleCreateEvent}
                                                    />,
                                                ]}
                                            />
                                        ) : (
                                            <Trans
                                                i18nKey="dashboard__empty_state__subtitle_tasks_only"
                                                ns="dashboard"
                                                components={[
                                                    <LinkText
                                                        key="create-task"
                                                        as="button"
                                                        onClick={handleCreateTask}
                                                    />,
                                                ]}
                                            />
                                        )}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    )}
                    {!isEmpty && (
                        <div className="flex flex-col gap-2 w-full">
                            <DashboardActivitiesList
                                title={t('activities__tasks_title', { count: taskCount, ns: 'dashboard' })}
                                totalCount={taskCount}
                                items={visibleTasks}
                                getItemKey={(task) => task.taskId}
                                renderItem={(task) => {
                                    const customer = task.links.find((link) => link.modelType === 'customer');

                                    return (
                                        <DashboardActivityItem
                                            icon={CircleCheck}
                                            primaryText={<RichTextViewer content={task.descriptionRich} />}
                                            onClick={() => {
                                                handleTaskClick(task);
                                            }}
                                            secondaryText={
                                                customer ? `${customer.modelId} ${customer.title}` : undefined
                                            }
                                            tail={<TaskDueStatusText task={task} />}
                                        />
                                    );
                                }}
                                onMoreClick={() => {
                                    sendEvent('home_show_all_clicked', { section: 'tasks' });
                                    void navigate('/tasks');
                                }}
                                createTitle={t('activities__create_task', { ns: 'dashboard' })}
                                onCreate={handleCreateTask}
                            />
                            {areEventsEnabled && (
                                <DashboardActivitiesList
                                    title={t('activities__events_title', {
                                        count: eventsCount,
                                        ns: 'dashboard',
                                    })}
                                    totalCount={eventsCount}
                                    items={visibleEvents}
                                    getItemKey={(event) => event.event_id}
                                    renderItem={(event) => {
                                        return (
                                            <DashboardActivityItem
                                                icon={CalendarIcon}
                                                primaryText={
                                                    event.title ??
                                                    t('activities__event_title', { ns: 'dashboard' })
                                                }
                                                onClick={() => {
                                                    sendEvent('home_activity_item_opened', {
                                                        item_type: 'event',
                                                    });
                                                    void DialogStateManager.show(
                                                        enableNewEventPage
                                                            ? NewEventDetailsDialog
                                                            : EventDetailsDialog,
                                                        {
                                                            eventId: event.event_id,
                                                            date: requestDate,
                                                            analyticsSource: 'dashboard',
                                                        },
                                                    );
                                                }}
                                                secondaryText={
                                                    event.customer
                                                        ? `${event.customer.customerId} ${event.customer.customerName}`
                                                        : undefined
                                                }
                                                tail={
                                                    event.start_time &&
                                                    formatISOTimeForDisplay(event.start_time)
                                                }
                                            />
                                        );
                                    }}
                                    onMoreClick={() => {
                                        sendEvent('home_show_all_clicked', { section: 'events' });
                                        void navigate('/events');
                                    }}
                                    createTitle={t('activities__create_event', { ns: 'dashboard' })}
                                    onCreate={handleCreateEvent}
                                />
                            )}
                        </div>
                    )}
                </div>
            </DataStatBlock.Body>
        </DataStatBlock.Item>
    );
}
